import React, { useState } from 'react';
import { format } from 'date-fns';
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';
import type { TaskGroupCopy } from '../../types';
import { useTaskStore } from '../../stores/taskStore';
import { useAuthStore } from '../../stores/authStore';

// Priority color helper — matches TaskCard styling
const priorityColorMap: Record<string, string> = {
  LOW: 'text-gray-400',
  MEDIUM: 'text-blue-500',
  HIGH: 'text-orange-500',
  URGENT: 'text-red-500',
};

// ============================================================================
// Sub-component: Individual copy item (compact + expandable)
// ============================================================================
interface TaskGroupCopyItemProps {
  copy: TaskGroupCopy;
  isExpanded: boolean;
  onToggle: () => void;
}

const TaskGroupCopyItem: React.FC<TaskGroupCopyItemProps> = ({ copy, isExpanded, onToggle }) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const priorityColor = priorityColorMap[copy.priority] || 'text-gray-400';

  return (
    <div className="rounded-md bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 overflow-visible">
      {/* Collapsed row — always visible */}
      <div className="flex items-center gap-2 p-2">
        {/* Assignee avatar */}
        <div className="flex-shrink-0">
          {copy.assignee?.avatar ? (
            <img src={copy.assignee.avatar} alt="" className="w-5 h-5 rounded-full" />
          ) : (
            <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
              {copy.assignee?.firstName?.[0] || '?'}
            </div>
          )}
        </div>

        {/* Compact info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {copy.assignee?.firstName} {copy.assignee?.lastName}
            </span>
            {copy.completed ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            )}
          </div>
          {!copy.completed && copy.dueDate && (
            <span className="text-xs text-gray-500">
              {t('tasks.ui.due')}: {format(new Date(copy.dueDate), 'MMM d', { locale: dateFnsLocale })}
            </span>
          )}
          {copy.completed && copy.completedAt && (
            <span className="text-xs text-green-600 dark:text-green-400">
              ✓ {t('tasks.ui.completedAt')}: {format(new Date(copy.completedAt), 'MMM d', { locale: dateFnsLocale })}
            </span>
          )}
        </div>

        {/* Priority dot */}
        <span className={`text-xs font-semibold flex-shrink-0 ${priorityColor}`}>
          ● {copy.priority}
        </span>

        {/* Expand toggle */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label={isExpanded ? t('tasks.ui.collapseDetails') : t('tasks.ui.expandDetails')}
        >
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="ml-7 mb-2 px-2 text-xs text-gray-600 dark:text-gray-400 space-y-1 border-t border-gray-100 dark:border-gray-700/50 pt-1">
          <div>
            {t('tasks.ui.priority')}: <span className={`font-semibold ${priorityColor}`}>{copy.priority}</span>
          </div>
          {copy.dueDate && (
            <div>
              {t('tasks.ui.due')}: {format(new Date(copy.dueDate), 'EEEE, MMMM d, yyyy', { locale: dateFnsLocale })}
            </div>
          )}
          <div>
            {t('tasks.ui.created')}: {format(new Date(copy.createdAt), 'MMM d, yyyy', { locale: dateFnsLocale })}
          </div>
          {copy.completed && copy.completedBy && (
            <div>
              {t('tasks.ui.completedBy')}: {copy.completedBy.firstName} {copy.completedBy.lastName}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main component: Dropdown showing group copies for the creator
// ============================================================================
interface TaskGroupCopiesDropdownProps {
  taskId: string;
  creatorId: string;
}

export const TaskGroupCopiesDropdown: React.FC<TaskGroupCopiesDropdownProps> = ({
  taskId,
  creatorId,
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [expandedCopyId, setExpandedCopyId] = useState<string | null>(null);
  const { groupCopies, groupCopiesLoading, fetchGroupCopies } = useTaskStore();
  const { user } = useAuthStore();

  // Safety: should not happen since parent checks, but guard anyway
  if (creatorId !== user?.id) return null;

  // Lazy fetch on first open
  const handleToggleDropdown = async () => {
    if (!isDropdownOpen) {
      await fetchGroupCopies(taskId);
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const copies = groupCopies[taskId] || [];
  const isLoading = groupCopiesLoading[taskId] || false;
  const completedCount = copies.filter((c) => c.completed).length;

  return (
    <div>
      {/* Header toggle button */}
      <button
        onClick={handleToggleDropdown}
        className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
        aria-expanded={isDropdownOpen}
        aria-controls={`group-copies-${taskId}`}
      >
        <Users className="w-3.5 h-3.5" />
        {isLoading ? (
          <span>{t('tasks.ui.loadingCopies')}</span>
        ) : copies.length > 0 ? (
          <span>
            {copies.length} {copies.length === 1 ? t('tasks.ui.copySingular') : t('tasks.ui.copiesPlural')} · {completedCount}/{copies.length} {t('tasks.ui.completedLabel')}
          </span>
        ) : (
          <span>{t('tasks.ui.teamCopies')}</span>
        )}
        {isDropdownOpen ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Dropdown content */}
      {isDropdownOpen && (
        <div
          id={`group-copies-${taskId}`}
          className="mt-2 space-y-1.5 max-h-64 overflow-y-auto"
          role="list"
          aria-live="polite"
          aria-busy={isLoading}
        >
          {isLoading ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 py-2">
              {t('tasks.ui.loadingTeamCopies')}
            </div>
          ) : copies.length === 0 ? (
            <div className="text-xs text-gray-500 dark:text-gray-400 py-2">
              {t('tasks.ui.noCopiesYet')}
            </div>
          ) : (
            copies.map((copy) => (
              <TaskGroupCopyItem
                key={copy.id}
                copy={copy}
                isExpanded={expandedCopyId === copy.id}
                onToggle={() =>
                  setExpandedCopyId(expandedCopyId === copy.id ? null : copy.id)
                }
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
